import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import AdminService from "../../../service/AdminService";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import Loader from "../../loader/Loader";
import DateService from "../../../service/DateService";

const SessionList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() + 1))
      .toISOString()
      .split("T")[0],
  );
  const user = useSelector((state) => state.user.user);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Prepare dates for API request
      const start = new Date(startDate);
      start.setHours(12, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(12, 0, 0, 0);

      // Fetch sessions with date filters
      const sessionsResponse = await AdminService.getSessionList(
        user?.access_token,
        -1,
        start.toISOString().split("T")[0],
        end.toISOString().split("T")[0],
      );
      setSessions(sessionsResponse.data || []);
    } catch (error) {
      console.error("Error fetching sessions data:", error);
      toast.error("Error fetching sessions data", { duration: 4000 });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.access_token, startDate, endDate]);

  const handleAdd = () => {
    navigate(`/admin/sessions/add`);
  };

  const handleEdit = (session) => {
    navigate(`/admin/sessions/edit/${session.sessionId}`);
  };

  const handleDateChange = (type, value) => {
    if (type === "start") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  return (
    <>
      <div>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-bold text-gray-900">Program Sessions</h1>
          {user?.role?.roleId === "ROLE_ADMIN" && (
            <Button color="primary" onClick={handleAdd} className="px-4 py-2">
              Add Session
            </Button>
          )}
        </div>

        <Card className="shadow-sm mb-4">
          <CardBody>
            <Row>
              <Col sm={6} md={3} className="mb-4">
                <FormGroup>
                  <Label className="text-xs font-medium text-gray-700">
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => handleDateChange("start", e.target.value)}
                    className="mt-1"
                  />
                </FormGroup>
              </Col>
              <Col sm={6} md={3} className="mb-4">
                <FormGroup>
                  <Label className="text-xs font-medium text-gray-700">
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => handleDateChange("end", e.target.value)}
                    className="mt-1"
                  />
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {sessions.length > 0 ? (
          <Card className="shadow-sm">
            <CardBody>
              <Table bordered hover responsive className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brief
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Selling Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visibility
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trimester
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Start Wk
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      End Wk
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Session Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Trainer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sessions.map((session, idx) => (
                    <tr
                      key={session.sessionId || idx}
                      className={
                        session.sessionStatus?.sessionStatusId === 2
                          ? "bg-red-100"
                          : ""
                      }
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.imageUrl && (
                          <img
                            src={session.imageUrl}
                            alt={session.title}
                            style={{
                              width: "auto",
                              height: "50px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.title}
                      </td>
                      <td
                        className="px-6 py-4 text-sm text-gray-500"
                        style={{
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {session.brief}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.sessionType?.sessionTypeId === 1
                          ? "-----"
                          : "₹" + session.price}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.sessionType?.sessionTypeId === 1
                          ? "-----"
                          : "₹" + session.sellingPrice}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.sessionCategory === 1 ? "Prenatal" : session.sessionCategory === 2 ? "Postnatal" : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.visibilityRule === 1 ? "Trimester" : session.visibilityRule === 2 ? "Week Based" : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(session.sessionCategory === 1 && session.visibilityRule === 1) ? session.trimesterNo : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.visibilityRule === 2 || session.sessionCategory === 2 ? session.startWeekNumber : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.visibilityRule === 2 || session.sessionCategory === 2 ? session.endWeekNumber : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.sessionType?.sessionTypeName}
                        {session.masterSessionId ? " (Child of Group)" : ""}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {DateService.formatDate(session.sessionDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.sessionStartTime}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.sessionDuration} mins
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.trainer?.trainerName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.sessionStatus?.sessionStatusDesc}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.active ? "Yes" : "No"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user?.role?.roleId === "ROLE_ADMIN" && (
                          <Button
                            color="info"
                            size="sm"
                            className="mr-2"
                            disabled={
                              session.sessionStatus?.sessionStatusId === 2
                            }
                            onClick={() => handleEdit(session)}
                          >
                            Edit
                          </Button>
                        )}
                        {session.sessionStatus?.sessionStatusId === 1 && (
                          <>
                            <a
                              href={session.zoomJoinUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 mx-1"
                            >
                              Join Meeting
                            </a>
                            <a
                              href={session.zoomStartUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 mx-1"
                            >
                              Start Meeting
                            </a>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        ) : (
          <Card className="shadow-sm">
            <CardBody>
              <p className="text-center text-gray-500">No sessions found</p>
            </CardBody>
          </Card>
        )}
      </div>
      {isLoading && <Loader />}
    </>
  );
};

export default SessionList;
